// src/lib/kwykedit-api.ts — typed fetch helpers for the KWYKEDIT backend.
//
// Auth is an httpOnly cookie session, so every request uses
// fetch(..., { credentials: 'include' }) and JSON bodies.
// All helpers throw ApiError on !ok (carrying the server error code).
//
// NOTE — backend shape mismatches vs the original frontend spec:
//   * GET /api/auth/me requires auth and returns the user object directly
//     ({ id, email, created_at, is_admin }); it 401s when signed out — it
//     does NOT return { user: null }.
//   * GET /api/billing/status returns
//     { has_access, base_cents, subscription, comp } — there is no top-level
//     `reason` / `expires_at`. Use billingReasonText() / billingExpiresAt()
//     below to derive display values.
//   * POST /api/billing/checkout returns
//     { base_cents, first_month_cents, first_month_only, discount, paypal }
//     where paypal = { stub, mode, configured, approval_url, note } — there is
//     no top-level `price_cents` / `paypal_approve_url`. Until PayPal is live
//     approval_url is always null.

export class ApiError extends Error {
  status: number
  code?: string

  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

interface ErrorBody {
  code?: unknown
  error?: unknown
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      credentials: 'include',
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    })
  } catch {
    throw new ApiError(0, 'Network error — could not reach the server')
  }
  let body: ErrorBody | null = null
  try {
    body = (await res.json()) as ErrorBody
  } catch {
    body = null
  }
  if (!res.ok) {
    const code = typeof body?.code === 'string' ? body.code : undefined
    const message =
      typeof body?.error === 'string' ? body.error : `Request failed (${res.status})`
    throw new ApiError(res.status, message, code)
  }
  return body as T
}

function post<T>(path: string, data?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: JSON.stringify(data ?? {}),
  })
}

function put<T>(path: string, data: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(data) })
}

// --- auth -----------------------------------------------------------------

export interface AuthUser {
  id: number
  email: string
  created_at: number
  is_admin: boolean
}

export function postSignup(email: string, password: string): Promise<AuthUser> {
  return post<AuthUser>('/api/auth/signup', { email, password })
}

export function postLogin(email: string, password: string): Promise<AuthUser> {
  return post<AuthUser>('/api/auth/login', { email, password })
}

export function postLogout(): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>('/api/auth/logout')
}

/** Throws ApiError(401) when signed out. */
export function getMe(): Promise<AuthUser> {
  return apiFetch<AuthUser>('/api/auth/me')
}

/** Same as getMe() but returns null instead of throwing on 401. */
export async function getMeOptional(): Promise<AuthUser | null> {
  try {
    return await getMe()
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null
    throw err
  }
}

// --- billing --------------------------------------------------------------

/** Condensed access reason (the backend does not return one directly). */
export type BillingReason = 'active' | 'comp' | 'past_due' | 'expired' | 'none'

export interface BillingSubscription {
  status: string
  current_period_end: number | null
  grace_until: number | null
  in_grace: boolean
}

export interface BillingComp {
  expires_at: number | null
  reason: string | null
}

export interface BillingStatus {
  has_access: boolean
  base_cents: number
  subscription: BillingSubscription | null
  comp: BillingComp | null
}

export function getBillingStatus(): Promise<BillingStatus> {
  return apiFetch<BillingStatus>('/api/billing/status')
}

export interface CheckoutDiscount {
  code: string
  percent_off: number
  amount_off_cents: number
}

export interface CheckoutResponse {
  approval_url: string | null
  paypal_subscription_id: string | null
  base_cents: number
  first_month_cents: number
  first_month_only: boolean
  discount: CheckoutDiscount | null
  // Legacy stub shape (pre-REST wiring) — absent from live responses.
  paypal?: {
    stub: boolean
    mode: string
    configured: boolean
    approval_url: string | null
    note: string
  } | null
}

/** Approval URL regardless of response shape (live flat vs legacy nested). */
export function checkoutApprovalUrl(q: CheckoutResponse | null): string | null {
  if (!q) return null
  return q.approval_url ?? q.paypal?.approval_url ?? null
}

export function postCheckout(discountCode?: string): Promise<CheckoutResponse> {
  return post<CheckoutResponse>('/api/billing/checkout', {
    discount_code: discountCode ?? '',
  })
}

/** Creates the PayPal subscription and returns the approval URL. Call only on Continue. */
export function postSubscribe(discountCode?: string): Promise<CheckoutResponse> {
  return post<CheckoutResponse>('/api/billing/subscribe', {
    discount_code: discountCode ?? '',
  })
}

/** Derive the condensed reason enum from a live status payload. */
export function billingReason(status: BillingStatus): BillingReason {
  if (status.comp) return 'comp'
  const sub = status.subscription
  if (!sub) return 'none'
  if (sub.status === 'active' || sub.status === 'trialing') return 'active'
  if (sub.status === 'past_due' && sub.in_grace) return 'past_due'
  return 'expired'
}

/** Best-known access expiry (comp expiry, period end, or grace end). */
export function billingExpiresAt(status: BillingStatus): number | null {
  if (status.comp?.expires_at) return status.comp.expires_at
  const sub = status.subscription
  if (!sub) return null
  return sub.grace_until ?? sub.current_period_end
}

/** Human-readable reason line for the Renew screen. */
export function billingReasonText(status: BillingStatus): string {
  const reason = billingReason(status)
  const expiry = billingExpiresAt(status)
  const when = expiry ? ` (until ${new Date(expiry * 1000).toLocaleDateString()})` : ''
  switch (reason) {
    case 'active':
      return 'Your subscription is active.'
    case 'comp':
      return `You have complimentary access${when}.`
    case 'past_due':
      return `Your last payment failed — access ends ${expiry ? `on ${new Date(expiry * 1000).toLocaleDateString()}` : 'soon'}. Renew to stay unlocked.`
    case 'expired': {
      const raw = status.subscription?.status ?? 'expired'
      const friendly =
        raw === 'pending_approval'
          ? 'waiting for PayPal confirmation'
          : raw === 'past_due'
            ? 'past due'
            : raw === 'cancelled'
              ? 'cancelled'
              : 'expired'
      return `Your subscription is ${friendly}. Renew to keep editing.`
    }
    case 'none':
    default:
      return 'KWYKEDIT is $3/month. Subscribe to unlock the editor.'
  }
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

// --- admin: users ----------------------------------------------------------

export interface AdminUserRow {
  id: number
  email: string
  created_at: number
  sub_status: string
  in_grace: boolean
  has_comp: boolean
  comp_expires_at: number | null
  closed: boolean
}

export function adminListUsers(): Promise<AdminUserRow[]> {
  return apiFetch<AdminUserRow[]>('/api/admin/users')
}

// --- admin: offers (discount_codes) ----------------------------------------

export interface Offer {
  id: number
  code: string
  percent_off: number
  amount_off_cents: number
  first_month_only: number
  expires_at: number | null
  max_uses: number | null
  uses: number
  affiliate_id: string | null
  active: number
}

export interface OfferInput {
  code: string
  percent_off?: number
  amount_off_cents?: number
  first_month_only?: boolean
  expires_at?: number | null
  max_uses?: number | null
  affiliate_id?: string | null
  active?: boolean
}

export function adminListOffers(): Promise<Offer[]> {
  return apiFetch<Offer[]>('/api/admin/offers')
}

export function adminCreateOffer(input: OfferInput): Promise<Offer> {
  return post<Offer>('/api/admin/offers', input)
}

export function adminUpdateOffer(id: number, patch: Partial<OfferInput>): Promise<Offer> {
  return put<Offer>(`/api/admin/offers/${id}`, patch)
}

export function adminDeleteOffer(id: number): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/api/admin/offers/${id}`, { method: 'DELETE' })
}

// --- admin: comps (entitlements type='comp') --------------------------------

export interface Comp {
  id: number
  user_id: number
  type: string
  reason: string | null
  granted_by: string | null
  granted_at: number
  expires_at: number | null
  revoked: number
  renewed_count: number
  email?: string
}

export function adminListComps(): Promise<Comp[]> {
  return apiFetch<Comp[]>('/api/admin/comps')
}

export function adminGrantComp(input: {
  user_email: string
  reason?: string
  months?: number
}): Promise<Comp> {
  return post<Comp>('/api/admin/comps', input)
}

export function adminRevokeComp(id: number): Promise<Comp> {
  return post<Comp>(`/api/admin/comps/${id}/revoke`)
}

/** Extend +12mo from max(expires_at, now); also un-revokes. */
export function adminExtendComp(id: number): Promise<Comp> {
  return post<Comp>(`/api/admin/comps/${id}/extend`)
}

export function adminExpiringComps(): Promise<Comp[]> {
  return apiFetch<Comp[]>('/api/admin/expiring-comps')
}

// --- admin: paypal settings -------------------------------------------------
// Secret is write-only: GET never returns it, only has_secret.

export interface PaypalSettings {
  client_id: string | null
  mode: string
  updated_at: number | null
  has_secret: boolean
  webhook_id: string | null
  product_id: string | null
  plan_id: string | null
}

export function adminGetPaypalSettings(): Promise<PaypalSettings> {
  return apiFetch<PaypalSettings>('/api/admin/paypal-settings')
}

export function adminUpdatePaypalSettings(input: {
  client_id?: string
  secret?: string
  mode?: string
  webhook_id?: string
}): Promise<{ ok: boolean; mode: string }> {
  return put<{ ok: boolean; mode: string }>('/api/admin/paypal-settings', input)
}

// --- public: affiliate applications ------------------------------------------

export type AffiliateKind = 'promo-artist' | 'company' | 'other'

export interface AffiliateApplyInput {
  name: string
  email: string
  kind: AffiliateKind
  about: string
  heard_from: string
}

export function postAffiliateApply(input: AffiliateApplyInput): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>('/api/contact/affiliates/apply', {
    name: input.name,
    email: input.email,
    kind: input.kind,
    about: input.about,
    heard_from: input.heard_from,
  })
}

// --- admin: affiliate applications --------------------------------------------

export interface AffiliateApplication {
  id: number
  name: string
  email: string
  kind: string
  about: string
  heard_from: string
  reviewed: number
  created_at: number
}

export function adminListAffiliates(): Promise<AffiliateApplication[]> {
  return apiFetch<AffiliateApplication[]>('/api/admin/affiliates')
}

export function adminReviewAffiliate(id: number, reviewed: boolean): Promise<AffiliateApplication> {
  return apiFetch<AffiliateApplication>(`/api/admin/affiliates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ reviewed }),
  })
}

export function adminDeleteAffiliate(id: number): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/api/admin/affiliates/${id}`, { method: 'DELETE' })
}

// --- auth: passwords -----------------------------------------------------------

export function postChangePassword(currentPassword: string, newPassword: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>('/api/auth/password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

export function postResetRequest(email: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>('/api/auth/reset/request', { email })
}

export function postResetConfirm(token: string, newPassword: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>('/api/auth/reset/confirm', { token, new_password: newPassword })
}

export function postReactivate(email: string, password: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>('/api/auth/reactivate', { email, password })
}

export function deleteAccount(): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('/api/auth/account', { method: 'DELETE' })
}

// --- admin: users ----------------------------------------------------------------

export function adminTempPassword(id: number): Promise<{ ok: boolean; temp_password: string }> {
  return post<{ ok: boolean; temp_password: string }>(`/api/admin/users/${id}/temp-password`, {})
}

export function adminReopenUser(id: number): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>(`/api/admin/users/${id}/reopen`, {})
}
